import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { BsZoomIn, BsZoomOut } from "react-icons/bs";
import HTMLFlipBook from "react-pageflip";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { useDownloadPdfMutation } from "../../redux-toolkit/services/pdfService";
import { alertMessage } from "../../utils/AlertService";

// Set the workerSrc to the local worker script
GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

type TextItem = {
    str: string;
    width: number;
    height: number;
    transform: number[];
};

type PageData = {
    image: string;
    text: string;
    textItems: TextItem[];
    viewport: {
        width: number;
        height: number;
    };
};


const PDFViewerPage = () => {
    const { fileId } = useParams<{ fileId: string }>();
    const navigate = useNavigate();
    const flipBookRef = useRef<any>(null);
    const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
    const highlightCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]); // Separate refs for highlight canvases
    const renderTasks = useRef<(any | null)[]>([]); // Track ongoing render tasks
    const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null); // For debouncing zoom
    const checkFlipBookTimeoutRef = useRef<NodeJS.Timeout | null>(null); // For readiness check

    const [pdfBlob, setPdfBlob] = useState<string | null>(null);
    const [pdfDocument, setPdfDocument] = useState<any>(null);
    const [currentFlipPage, setCurrentFlipPage] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<number[]>([]);
    const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
    const [isMaximized, setIsMaximized] = useState(false);
    const [isLoadingPages, setIsLoadingPages] = useState(false);
    const [isFlipBookReady, setIsFlipBookReady] = useState(false);
    const [numPages, setNumPages] = useState(0);
    const [pagesData, setPagesData] = useState<PageData[]>([]);
    const [zoomLevel, setZoomLevel] = useState(1);

    const [downloadPdf, downloadPdfResponse] = useDownloadPdfMutation();

    const [baseWidth, setBaseWidth] = useState(1200);
    const [baseHeight, setBaseHeight] = useState(1600);

    const getFlipbookDimensions = useCallback(() => {
        return {
            width: baseWidth * zoomLevel,
            height: baseHeight * zoomLevel,
        };
    }, [baseWidth, baseHeight, zoomLevel]);

    // Load the PDF document
    useEffect(() => {
        if (!fileId) {
            console.error("No fileId provided");
            alertMessage("Invalid PDF URL.", "error");
            navigate("/");
            return;
        }

        setIsLoadingPages(true);
        downloadPdf(fileId)
            .unwrap()
            .then(async (response: any) => {
                const blob = new Blob([response], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                setPdfBlob(url);

                // Load PDF and extract pages as images and text with positions
                const loadingTask = getDocument(url);
                const pdf = await loadingTask.promise;
                const numPages = pdf.numPages;
                const pages: PageData[] = [];

                console.log(`Total pages in PDF: ${numPages}`);

                const scale = 1.0; // Same scale as used in viewport

                for (let i = 1; i <= numPages; i++) {
                    const page = await pdf.getPage(i);
                    const viewport = page.getViewport({ scale });

                    if (i === 1) {
                        setBaseWidth(viewport.width);
                        setBaseHeight(viewport.height);
                    }

                    // Render page to image
                    const canvas = document.createElement("canvas");
                    const context = canvas.getContext("2d");
                    canvas.height = viewport.height;
                    canvas.width = viewport.width;

                    await page.render({
                        canvasContext: context!,
                        viewport,
                    }).promise;

                    const image = canvas.toDataURL("image/png");

                    // Extract text and positions
                    const textContent = await page.getTextContent();
                    const pageText = textContent.items
                        .map((item: any) => item.str || "")
                        .join(" ")
                        .trim();

                    const textItems: TextItem[] = textContent.items
                        .filter((item: any) => item.str && item.str.trim())
                        .map((item: any) => ({
                            str: item.str.toLowerCase().trim(),
                            width: item.width * scale,
                            height: item.height * scale,
                            transform: item.transform.map((val: number) => val * scale),
                        }));

                    console.log(`Page ${i} text:`, pageText);
                    console.log(`Page ${i} text items:`, textItems);

                    pages.push({
                        image,
                        text: pageText.toLowerCase(),
                        textItems,
                        viewport: {
                            width: viewport.width,
                            height: viewport.height,
                        },
                    });
                }

                console.log("Extracted pages data:", pages);
                setPagesData(pages);
                setIsLoadingPages(false);
            })
            .catch((error: any) => {
                console.error("Failed to load PDF:", error);
                setIsLoadingPages(false);
                alertMessage("Failed to load PDF.", "error");
                navigate("/");
            });

        return () => {
            if (pdfBlob) {
                URL.revokeObjectURL(pdfBlob);
            }
            // Cancel any ongoing render tasks on unmount
            renderTasks.current.forEach((task) => {
                if (task) {
                    try {
                        task.cancel();
                    } catch (e) {
                        console.warn("Error canceling render task on unmount:", e);
                    }
                }
            });
            // Clear the readiness check timeout
            if (checkFlipBookTimeoutRef.current) {
                clearTimeout(checkFlipBookTimeoutRef.current);
            }
        };
    }, [fileId, downloadPdf, navigate]);

    // Check if flipBookRef is ready
    useEffect(() => {
        if (pagesData.length > 0 && flipBookRef.current) {
            let attempts = 0;
            const maxAttempts = 100; // Increased to 10 seconds (100ms intervals)
            const interval = 100;

            const checkFlipBookReady = () => {
                attempts++;
                const pageFlipInstance = flipBookRef.current?.pageFlip?.();
                if (pageFlipInstance && typeof pageFlipInstance.flip === "function") {
                    console.log("FlipBook is ready after", attempts, "attempts!");
                    setIsFlipBookReady(true);
                } else if (attempts >= maxAttempts) {
                    console.warn("Max attempts reached. Marking FlipBook as ready with fallback navigation.");
                    setIsFlipBookReady(true); // Fallback to allow search even if pageFlip isn't fully ready
                } else {
                    console.log("FlipBook not ready yet, attempt", attempts);
                    checkFlipBookTimeoutRef.current = setTimeout(checkFlipBookReady, interval);
                }
            };

            console.log("Starting FlipBook readiness check...");
            checkFlipBookReady();
        }

        return () => {
            setIsFlipBookReady(false);
            if (checkFlipBookTimeoutRef.current) {
                clearTimeout(checkFlipBookTimeoutRef.current);
                checkFlipBookTimeoutRef.current = null;
            }
        };
    }, [pagesData]);

    // Force FlipBook to update when zoomLevel changes
    useEffect(() => {
        if (flipBookRef.current && isFlipBookReady) {
            const pageFlipInstance = flipBookRef.current.pageFlip();
            if (pageFlipInstance && typeof pageFlipInstance.updateFrom === "function") {
                console.log("Updating FlipBook dimensions for zoomLevel:", zoomLevel);
                pageFlipInstance.update();
            }
        }
    }, [zoomLevel, isFlipBookReady]);

    // Highlight search results
    useEffect(() => {
        highlightCanvasRefs.current.forEach((canvas, index) => {
            if (canvas && searchResults.includes(index) && searchQuery && pagesData.length > 0) {
                const pageData = pagesData[index];
                const { width, height } = pageData.viewport;

                // Calculate scale based on the rendering scale used
                const baseViewport = pageData.viewport;
                const scale = zoomLevel * (baseWidth / baseViewport.width);
                const scaleX = scale;
                const scaleY = scale;

                const query = searchQuery.toLowerCase().trim();
                console.log(`Processing highlights for page ${index}, query: "${query}"`);

                // Clear the highlight canvas
                canvas.width = canvas.width; // Reset the canvas
                const ctx = canvas.getContext("2d");

                pageData.textItems.forEach((item, itemIndex) => {
                    if (item.str.includes(query)) {
                        const x = item.transform[4] * scaleX;
                        const y = (height - item.transform[5]) * scaleY - (item.height * scaleY / 2);
                        const itemWidth = item.width * scaleX;
                        const itemHeight = item.height * scaleY;

                        ctx!.fillStyle =
                            currentSearchIndex === searchResults.indexOf(index)
                                ? "rgba(255, 0, 0, 0.5)"
                                : "rgba(255, 255, 0, 0.5)";
                        ctx!.fillRect(x, y, itemWidth, itemHeight);
                    }
                });
            }
        });
    }, [searchResults, searchQuery, pagesData, currentSearchIndex, zoomLevel]);

    const handleSearchInPdf = () => {
        if (!searchQuery) {
            setSearchResults([]);
            setCurrentSearchIndex(-1);
            alertMessage("Please enter a search term.", "warning");
            return;
        }

        if (isLoadingPages) {
            alertMessage("Please wait, PDF pages are still loading.", "warning");
            return;
        }

        if (pagesData.length === 0) {
            alertMessage("PDF pages are not loaded yet.", "error");
            return;
        }

        if (!isFlipBookReady) {
            alertMessage("FlipBook is not ready yet. Please wait.", "warning");
            return;
        }

        const query = searchQuery.toLowerCase().trim();
        const results: number[] = [];
        pagesData.forEach((page, index) => {
            if (page.text.includes(query)) {
                results.push(index);
            }
        });

        if (results.length === 0) {
            alertMessage("No matches found.", "info");
        } else {
            alertMessage(`${results.length} match(es) found.`, "success");
        }

        setSearchResults(results);
        setCurrentSearchIndex(results.length > 0 ? 0 : -1);

        if (results.length > 0) {
            const pageToJump = results[0];
            if (flipBookRef.current && typeof flipBookRef.current.pageFlip === "function") {
                const pageFlipInstance = flipBookRef.current.pageFlip();
                if (pageFlipInstance && typeof pageFlipInstance.flip === "function") {
                    pageFlipInstance.flip(pageToJump);
                    setCurrentFlipPage(pageToJump);
                } else {
                    console.warn("PageFlip instance not fully ready, using fallback navigation");
                    setCurrentFlipPage(pageToJump);
                }
            } else {
                setCurrentFlipPage(pageToJump);
            }
        }
    };

    const handleNextSearchResult = () => {
        if (searchResults.length === 0) return;
        const nextIndex = (currentSearchIndex + 1) % searchResults.length;
        setCurrentSearchIndex(nextIndex);
        const pageToJump = searchResults[nextIndex];
        if (flipBookRef.current && typeof flipBookRef.current.pageFlip === "function") {
            const pageFlipInstance = flipBookRef.current.pageFlip();
            if (pageFlipInstance && typeof pageFlipInstance.flip === "function") {
                pageFlipInstance.flip(pageToJump);
                setCurrentFlipPage(pageToJump);
            } else {
                console.warn("PageFlip instance not fully ready, using fallback navigation");
                setCurrentFlipPage(pageToJump);
            }
        } else {
            setCurrentFlipPage(pageToJump);
        }
    };

    const handlePrevSearchResult = () => {
        if (searchResults.length === 0) return;
        const prevIndex = (currentSearchIndex - 1 + searchResults.length) % searchResults.length;
        setCurrentSearchIndex(prevIndex);
        const pageToJump = searchResults[prevIndex];
        if (flipBookRef.current && typeof flipBookRef.current.pageFlip === "function") {
            const pageFlipInstance = flipBookRef.current.pageFlip();
            if (pageFlipInstance && typeof pageFlipInstance.flip === "function") {
                pageFlipInstance.flip(pageToJump);
                setCurrentFlipPage(pageToJump);
            } else {
                console.warn("PageFlip instance not fully ready, using fallback navigation");
                setCurrentFlipPage(pageToJump);
            }
        } else {
            setCurrentFlipPage(pageToJump);
        }
    };

    const handlePrevPage = () => {
        if (flipBookRef.current && typeof flipBookRef.current.pageFlip === "function") {
            const pageFlipInstance = flipBookRef.current.pageFlip();
            if (pageFlipInstance && typeof pageFlipInstance.flipPrev === "function") {
                pageFlipInstance.flipPrev();
            } else {
                if (currentFlipPage > 0) {
                    setCurrentFlipPage(currentFlipPage - 1);
                }
            }
        }
    };

    const handleNextPage = () => {
        if (flipBookRef.current && typeof flipBookRef.current.pageFlip === "function") {
            const pageFlipInstance = flipBookRef.current.pageFlip();
            if (pageFlipInstance && typeof pageFlipInstance.flipNext === "function") {
                pageFlipInstance.flipNext();
            } else {
                if (currentFlipPage < pagesData.length - 1) {
                    setCurrentFlipPage(currentFlipPage + 1);
                }
            }
        }
    };

    const handleZoomIn = () => {
        const newZoom = Math.min(zoomLevel + 0.25, 3);
        setZoomLevel(newZoom);
        console.log("Zooming in, new zoom level:", newZoom);
    };

    const handleZoomOut = () => {
        const newZoom = Math.max(zoomLevel - 0.25, 0.5);
        setZoomLevel(newZoom);
        console.log("Zooming out, new zoom level:", newZoom);
    };

    const toggleMaximize = () => {
        setIsMaximized((prev) => !prev);
    };

    const handleClose = () => {
        setPdfBlob(null);
        setPagesData([]);
        setSearchQuery("");
        setSearchResults([]);
        setCurrentSearchIndex(-1);
        setIsMaximized(false);
        setIsLoadingPages(false);
        setIsFlipBookReady(false);
        setZoomLevel(1);
        if (pdfBlob) {
            URL.revokeObjectURL(pdfBlob);
        }
        window.close();
    };

    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: "100vh",
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                backgroundColor: "#f5f5f5",
                padding: "20px",
            }}
        >
            <div
                style={{
                    width: "100%",
                    maxWidth: "1200px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                }}
            >
                <div className="d-flex align-items-center">
                    <div className="input-group me-2" style={{ width: "300px" }}>
                        <Form.Control
                            type="text"
                            placeholder="Search in PDF..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === "Enter") handleSearchInPdf();
                            }}
                            disabled={isLoadingPages || !isFlipBookReady}
                        />
                        <Button
                            variant="outline-primary"
                            onClick={handleSearchInPdf}
                            disabled={isLoadingPages || !isFlipBookReady}
                        >
                            Search
                        </Button>
                    </div>
                    {searchResults.length > 0 && (
                        <div className="d-flex align-items-center me-2">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handlePrevSearchResult}
                                disabled={currentSearchIndex === -1 || !isFlipBookReady}
                            >
                                Prev
                            </Button>
                            <span className="mx-2">
                                {currentSearchIndex + 1} of {searchResults.length}
                            </span>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleNextSearchResult}
                                disabled={currentSearchIndex === -1 || !isFlipBookReady}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                    <div className="d-flex align-items-center me-2">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleZoomOut}
                            disabled={zoomLevel <= 0.5}
                        >
                            <BsZoomOut size={16} />
                        </Button>
                        <span className="mx-2">{Math.round(zoomLevel * 100)}%</span>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleZoomIn}
                            disabled={zoomLevel >= 3}
                        >
                            <BsZoomIn size={16} />
                        </Button>
                    </div>
                    <Button
                        variant="outline-secondary"
                        className="me-2"
                        onClick={toggleMaximize}
                    >
                        {isMaximized ? "Minimize" : "Maximize"}
                    </Button>
                </div>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </div>

            <div
                style={{
                    width: "100%",
                    maxWidth: "1200px",
                    overflow: "auto",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                {isLoadingPages ? (
                    <div>Loading PDF pages...</div>
                ) : pagesData.length > 0 ? (
                    <>
                        <HTMLFlipBook
                            width={getFlipbookDimensions().width}
                            height={getFlipbookDimensions().height}
                            size="stretch"
                            minWidth={getFlipbookDimensions().width}
                            maxWidth={getFlipbookDimensions().width}
                            minHeight={getFlipbookDimensions().height}
                            maxHeight={getFlipbookDimensions().height}
                            drawShadow={true}
                            flippingTime={1000}
                            usePortrait={true}
                            startPage={currentFlipPage}
                            className="flip-book"
                            style={{ flex: "none", marginTop: "None" }}
                            ref={flipBookRef}
                            useMouseEvents={true}
                            onFlip={(e: any) => {
                                setCurrentFlipPage(e.data);
                            }}
                            onInit={(data: any) => {
                                console.log("FlipBook onInit fired!", data);
                                const pageFlipInstance = flipBookRef.current.pageFlip();
                                console.log("PageFlip instance in onInit:", pageFlipInstance);
                            }}
                            startZIndex={0}
                            autoSize={false}
                            maxShadowOpacity={0}
                            showCover={false}
                            mobileScrollSupport={false}
                            clickEventForward={false}
                            swipeDistance={0}
                            showPageCorners={false}
                            disableFlipByClick={false}
                        >
                            {pagesData.map((page, index) => (
                                <div className="demoPage" key={index} style={{ position: "relative" }}>
                                    <img
                                        src={page.image}
                                        alt={`Page ${index + 1}`}
                                        style={{
                                            width: `${getFlipbookDimensions().width}px`,
                                            height: `${getFlipbookDimensions().height}px`,
                                            objectFit: "contain",
                                        }}
                                    />
                                    {searchResults.includes(index) && searchQuery && (
                                        <canvas
                                            width={getFlipbookDimensions().width}
                                            height={getFlipbookDimensions().height}
                                            style={{
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                width: `${getFlipbookDimensions().width}px`,
                                                height: `${getFlipbookDimensions().height}px`,
                                                pointerEvents: "none",
                                            }}
                                            ref={(el) => (canvasRefs.current[index] = el)}
                                        />
                                    )}
                                    <canvas
                                        width={getFlipbookDimensions().width}
                                        height={getFlipbookDimensions().height}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: `${baseWidth * zoomLevel}px`,
                                            height: `${baseHeight * zoomLevel}px`,
                                            pointerEvents: "none",
                                            opacity: 0.3,
                                        }}
                                        ref={(canvas) => {
                                            if (canvas) {
                                                const ctx = canvas.getContext("2d");
                                                if (ctx) {
                                                    ctx.fillStyle = "#3997ff";
                                                    ctx.font = `${20 * zoomLevel}px Arial`;
                                                    for (let x = 0; x < canvas.width; x += 100 * zoomLevel) {
                                                        for (let y = 0; y < canvas.height; y += 100 * zoomLevel) {
                                                            ctx.fillText("Kalki Agent", x, y);
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            ))}
                        </HTMLFlipBook>
                        <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                            <Button
                                variant="primary"
                                onClick={handlePrevPage}
                                disabled={currentFlipPage === 0}
                            >
                                Previous
                            </Button>
                            <span className="mt-1">
                                Page {currentFlipPage + 1} of {numPages}
                            </span>
                            <Button
                                variant="primary"
                                onClick={handleNextPage}
                                disabled={currentFlipPage >= numPages - 1}
                            >
                                Next
                            </Button>
                        </div>
                    </>
                ) : (
                    <div>No pages loaded.</div>
                )}
            </div>
        </div>
    );
};

export default PDFViewerPage;